/**
 * Skeleton Components
 *
 * Loading placeholder components with pulse animation.
 */

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton element - a simple animated placeholder
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
  );
}

/**
 * Text line skeleton with customizable width
 */
export function SkeletonText({ className = '', width = 'w-full' }: SkeletonProps & { width?: string }) {
  return <Skeleton className={`h-4 ${width} ${className}`} />;
}

/**
 * Avatar skeleton. Square, because the retheme's avatars are square — a round
 * placeholder that resolves into a square block reads as a layout glitch.
 */
export function SkeletonCircle({ className = '', size = 'w-12 h-12' }: SkeletonProps & { size?: string }) {
  return <Skeleton className={`skeleton-avatar ${size} ${className}`} />;
}

/**
 * Event card loading skeleton
 */
export function EventCardSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  );
}

/**
 * Crew/User card loading skeleton
 */
export function CrewCardSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
      <div className="flex items-start gap-4 mb-4">
        <div className="skeleton-avatar w-12 h-12 bg-gray-200"></div>
        <div className="flex-1">
          <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  );
}

/**
 * Table row loading skeleton
 */
export function TableRowSkeleton({ columns = 4 }: { columns?: number }) {
  return (
    <tr className="animate-pulse">
      {[...Array(columns)].map((_, i) => (
        <td key={i} className="px-6 py-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </td>
      ))}
    </tr>
  );
}

/**
 * Card grid skeleton - renders multiple card skeletons
 */
export function CardGridSkeleton({
  count = 6,
  variant = 'event'
}: {
  count?: number;
  variant?: 'event' | 'crew';
}) {
  const SkeletonComponent = variant === 'crew' ? CrewCardSkeleton : EventCardSkeleton;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(count)].map((_, i) => (
        <SkeletonComponent key={i} />
      ))}
    </div>
  );
}

export default Skeleton;
